import {createContext, FC, useContext} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {CommonEntityProvider, ICommonComponentProps} from '../../definitions/common.definitions';
import {fetch} from '../../shared/api/v1/client';
import type {components} from '../../shared/api/v1/openapi.types';

export type FacilityEntityT = components['schemas']['Facility'];

export type FacilitiesContextT = CommonEntityProvider<FacilityEntityT[]> & {
  open: (id: number) => Promise<FacilityEntityT>;
}

const context = createContext<FacilitiesContextT>(null!);
const FacilitiesContextProvider = context.Provider;
const useFacilities = () => useContext(context);

const FacilitiesProvider: FC<ICommonComponentProps> = (props) => {
  const {
    children,
  } = props;

  const queryClient = useQueryClient();

  const {
    isLoading,
    data = [],
  } = useQuery({
    queryKey: ['facilities', 'public'],
    queryFn: async () => {
      const response = await fetch.GET('/facilities/public');
      const error = (response as { error?: unknown }).error;
      if (error) {
        throw error;
      }
      return (response as { data?: FacilityEntityT[] }).data ?? [];
    },
  });

  const open = (id: number) => {
    return queryClient.fetchQuery({
      queryKey: ['facilities', id],
      queryFn: async () => {
        const response = await fetch.GET('/facilities/{id}', {
          params: {
            path: { id: String(id) },
          },
          headers: {
            Authorization: '',
          },
        });
        const error = (response as { error?: unknown }).error;
        if (error) {
          throw error;
        }
        const facility = (response as { data?: FacilityEntityT }).data;
        if (!facility) {
          throw new Error('Facility not found');
        }
        return facility;
      },
    });
  }

  const ctx: FacilitiesContextT = {
    open,
    data,
    isLoading,
  }

  return (
    <FacilitiesContextProvider
      value={ctx}
    >
      {children}
    </FacilitiesContextProvider>
  )
}

export default FacilitiesProvider;
export {
  FacilitiesProvider,
  useFacilities,
}